import {Spec} from 'immutability-helper';
import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {Protocol} from 'notebook/ELNConstants';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
// @ts-expect-error
import StepsEditor from 'notebook/protocols/editor/StepsEditor';
import STEPS_EDITOR_PLUGINS from 'notebook/protocols/editor/StepsEditorPlugins';
import {StepsEditorPlugin} from 'notebook/protocols/editor/StepsEditorTypes';
import * as StepConstants from 'notebook/protocols/StepConstants';
import {Step} from 'notebook/protocols/StepConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import withSpreadsheetEvaluation from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import * as Logging from 'util/Logging';
import {ElementKeyboardEvent} from 'util/Types';

const WrappedStepsEditor = withSpreadsheetEvaluation(StepsEditor);

export interface StepsEditorContainerProps {
  fileId: string;
  protocol: Protocol;
  updateProtocol?: (newProtocol: Protocol) => void;
  restoreState: (e: ElementKeyboardEvent, selectionRestoreData: SelectionRestoreData) => boolean;
  storeState: () => void;
  jumpTo: (data: {section: string; location: string}) => void;
  spellCheck: boolean;
  isLockedWithEditWarn: () => boolean;
  setRef: (component: StepsEditor) => void;
  rootRef: React.RefObject<HTMLDivElement>;
  requestSelection: (
    selectionRestoreData: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData}
  ) => void;
}

export default class StepsEditorContainer extends React.Component<StepsEditorContainerProps> {
  static propTypes = {
    fileId: PropTypes.string.isRequired,
    protocol: PropTypes.object.isRequired,
    storeState: PropTypes.func.isRequired,
    restoreState: PropTypes.func.isRequired,
    jumpTo: PropTypes.func.isRequired,
    updateProtocol: PropTypes.func, // omitted if locked
    spellCheck: PropTypes.bool.isRequired,
    isLockedWithEditWarn: PropTypes.func.isRequired,
    requestSelection: PropTypes.func.isRequired,
  };

  spreadsheetTypes = [StepConstants.StepLevel.TABLE];

  getPluginByType(type: string): StepsEditorPlugin | null {
    for (const plugin of STEPS_EDITOR_PLUGINS) {
      if (plugin.type === type) {
        return plugin;
      }
    }
    return null;
  }

  makeFullTableMap = (props: StepsEditorContainerProps): FullTableMap => {
    const tableSteps = _.where(this.spreadsheetRoot(props), {level: StepConstants.StepLevel.TABLE});
    const tableMap: FullTableMap = {};
    for (const step of Array.from(tableSteps)) {
      tableMap[step.table!.name] = step.table!;
    }
    return tableMap;
  };

  selectorsFromProps = (props: StepsEditorContainerProps): Protocol['selectors'] => props.protocol.selectors;

  getRoot = (props?: StepsEditorContainerProps): Protocol => (props || this.props).protocol;

  getItems = (root: Protocol): Protocol['steps'] => root.steps;

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
  getContent = (item: Step) => {
    const type = this.getType(item);
    const plugin = this.getPluginByType(type);
    return plugin!.getContent.call(this, item);
  };

  getSpreadsheetTableName = (item: Step): string => {
    const type = this.getType(item);
    const plugin = this.getPluginByType(type);
    assert(
      plugin != null && plugin.getSpreadsheetTableName !== undefined,
      `Plugin for type ${type} does not have getSpreadsheetTableName`
    );
    return plugin.getSpreadsheetTableName.call(this, item);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
  setContent = (type: string, content: object) => {
    const plugin = this.getPluginByType(type);
    return plugin!.setContent.call(this, content);
  };

  getType = (item: Step): Step['level'] => item.level;

  updateRoot = (newProtocol: Protocol): void => {
    if (this.props.updateProtocol) {
      this.props.updateProtocol(newProtocol);
    } else {
      // Protocol is likely locked - updateProtocol is not provided
      Logging.warn(
        'StepsEditorContainer: updateProtocol is not available, likely because the protocol is locked'
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsAccessor = (op: {[i: number]: Spec<any>}): Spec<any> => ({steps: op});

  spreadsheetRoot = (props: StepsEditorContainerProps): Protocol['steps'] => props.protocol.steps;

  getCid = (item: Step): string => item.cid;

  getNoteRuntimeSnapshot = (): null => /* TODO(BNCH-14712) */ null;

  override render(): JSX.Element {
    return (
      <WrappedStepsEditor
        {...this.props}
        setRef={this.props.setRef}
        rootRef={this.props.rootRef}
        getCid={this.getCid}
        getContent={this.getContent}
        getSpreadsheetTableName={this.getSpreadsheetTableName}
        getItems={this.getItems}
        getRoot={this.getRoot}
        getType={this.getType}
        itemsAccessor={this.itemsAccessor as (op: {[i: number]: Spec<unknown>}) => Spec<unknown>}
        makeFullTableMap={this.makeFullTableMap}
        selectorsFromProps={this.selectorsFromProps}
        setContent={this.setContent}
        spreadsheetRoot={this.spreadsheetRoot}
        spreadsheetTypes={this.spreadsheetTypes}
        updateRoot={this.updateRoot}
        getNoteRuntimeSnapshot={this.getNoteRuntimeSnapshot}
      />
    );
  }
}
